/* Batch 15 · N4 (F-118) — the in-app dialog host (see lib/appDialog.js). One component for confirm / prompt / alert:
   title, message (line breaks kept), Cancel / OK, optional text field with the "required" and "must change" rules.
   EN + AR through the app's i18n; RTL follows <html dir>. */
import React, { useEffect, useRef, useState } from "react";
import { registerAppDialogHandler } from "@/lib/appDialog";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function AppDialogHost() {
  const { t } = useI18n();
  const [req, setReq] = useState(null);
  const [value, setValue] = useState("");
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    registerAppDialogHandler((r) => new Promise((resolve) => {
      if (resolveRef.current) resolveRef.current(r.kind === "prompt" ? null : false);   // a second ask closes the first
      resolveRef.current = resolve;
      setValue(r.kind === "prompt" ? (r.defaultValue || "") : "");
      setReq(r);
      setTimeout(() => { try { inputRef.current?.focus(); inputRef.current?.select(); } catch { /* no input */ } }, 30);
    }));
    return () => registerAppDialogHandler(null);
  }, []);

  const finish = (answer) => {
    const r = resolveRef.current; resolveRef.current = null;
    setReq(null); setValue("");
    if (r) r(answer);
  };
  if (!req) return null;
  const isPrompt = req.kind === "prompt";
  const trimmed = value.trim();
  const blank = isPrompt && req.required !== false && !trimmed;
  const unchanged = isPrompt && req.mustChange && trimmed === String(req.defaultValue || "").trim();
  const okDisabled = blank || unchanged;
  const ok = () => { if (okDisabled) return; finish(isPrompt ? value : true); };
  const cancel = () => finish(isPrompt ? null : (req.kind === "alert" ? undefined : false));
  const title = req.title || t(isPrompt ? "appDialog.promptTitle" : req.kind === "alert" ? "appDialog.alertTitle" : "appDialog.confirmTitle");
  const Field = req.multiline ? "textarea" : "input";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="max-w-md" hideCloseButton data-testid="app-dialog" data-kind={req.kind}>
        <DialogHeader className="text-start sm:text-start">
          <DialogTitle data-testid="app-dialog-title">{title}</DialogTitle>
          <DialogDescription className="whitespace-pre-line text-start text-sm text-foreground" data-testid="app-dialog-message">{req.message}</DialogDescription>
        </DialogHeader>
        {isPrompt && (
          <div className="space-y-1">
            <Field ref={inputRef} className="w-full rounded border bg-background px-2 py-1.5 text-sm" rows={req.multiline ? 4 : undefined}
                   value={value} placeholder={req.placeholder || ""} onChange={(e) => setValue(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter" && !req.multiline) { e.preventDefault(); ok(); } }} data-testid="app-dialog-input" />
            {blank ? <div className="text-[11px] text-muted-foreground" data-testid="app-dialog-rule">{t("appDialog.required")}</div>
              : unchanged ? <div className="text-[11px] text-muted-foreground" data-testid="app-dialog-rule">{t("appDialog.unchanged")}</div> : null}
          </div>
        )}
        <DialogFooter className="gap-2 sm:space-x-0">
          {req.kind !== "alert" && <Button variant="outline" onClick={cancel} data-testid="app-dialog-cancel">{req.cancelLabel || t("common.cancel")}</Button>}
          <Button variant={req.danger ? "destructive" : "default"} disabled={okDisabled} onClick={ok} data-testid="app-dialog-ok">{req.okLabel || t("common.ok")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
